import { useState } from "react";
import { Button } from "components/common/buttons/Button";
import { Modal, ModalBody, ModalHeader } from "components/common/modal";
import { useBudgetVersionDiff, useBudgetVersionDiffCategory } from "hooks/useBudget";
import { formatNumberWithCommas } from "utils";

interface BudgetVersionDiffPanelProps {
  open: boolean;
  onClose: () => void;
  projectId: string | number;
  documentId: string | number;
  compareBudgetId: number;
  /** The live version to compare against. Sent explicitly, so a budget with no primary works. */
  againstBudgetId: number | undefined;
  compareLabel: string;
  currencySymbol: string;
}

export const BudgetVersionDiffPanel = ({
  open,
  onClose,
  projectId,
  documentId,
  compareBudgetId,
  againstBudgetId,
  compareLabel,
  currencySymbol,
}: BudgetVersionDiffPanelProps) => {
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const diffQuery = useBudgetVersionDiff({
    projectId,
    documentId,
    budgetId: String(compareBudgetId),
    againstBudgetId,
    enabled: open,
  });
  const categoryDiffQuery = useBudgetVersionDiffCategory({
    projectId,
    documentId,
    budgetId: String(compareBudgetId),
    againstBudgetId,
    categoryName: selectedCategory,
    enabled: open && Boolean(selectedCategory),
  });

  const summary = diffQuery.data?.summary;
  const categories = diffQuery.data?.category_changes ?? [];
  const money = (value: number | null | undefined) =>
    `${currencySymbol}${formatNumberWithCommas(value ?? 0, true)}`;

  return (
    <Modal
      isOpen={open}
      onClose={() => {
        setSelectedCategory(null);
        onClose();
      }}
      maxWidth="max-w-3xl"
    >
      <ModalHeader
        title={
          selectedCategory
            ? `${selectedCategory}: line changes`
            : `Compare: ${compareLabel} vs live`
        }
        onClose={() => {
          setSelectedCategory(null);
          onClose();
        }}
      />
      <ModalBody>
        {selectedCategory ? (
          <div className="flex flex-col gap-3 text-left">
            <Button
              type="button"
              color="gray"
              size="sm"
              className="self-start"
              onClick={() => setSelectedCategory(null)}
            >
              ← Back to categories
            </Button>
            {categoryDiffQuery.isLoading ? (
              <p className="text-sm text-muted-foreground">Loading line items…</p>
            ) : categoryDiffQuery.isError ? (
              <p className="text-sm text-danger">Could not load category line diff.</p>
            ) : (
              <div className="flex flex-col gap-2">
                {(categoryDiffQuery.data?.line_changes ?? [])
                  .filter((line) => line.status !== "unchanged")
                  .map((line) => {
                    const lineLabel = line.account_number
                      ? `${line.account_number} · ${line.name}`
                      : line.name;
                    return (
                      <div
                        key={line.key}
                        className="rounded border border-border px-3 py-2 text-sm"
                      >
                        <div className="flex items-center justify-between gap-3">
                          <span className="min-w-0 truncate font-medium text-foreground">
                            {lineLabel}
                          </span>
                          <span className="shrink-0 text-muted-foreground">
                            {money(line.before_estimate)} → {money(line.after_estimate)}
                          </span>
                        </div>
                        <p className="mt-1 text-[11px] uppercase tracking-wide text-muted-foreground">
                          {line.status}
                        </p>
                        {line.field_changes?.length ? (
                          <ul className="mt-1 space-y-0.5 text-[12px] text-muted-foreground">
                            {line.field_changes.map((change) => (
                              <li key={`${lineLabel}-${change.field}`}>
                                {change.field}: {String(change.before ?? "—")} →{" "}
                                {String(change.after ?? "—")}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </div>
                    );
                  })}
                {(categoryDiffQuery.data?.line_changes ?? []).filter(
                  (line) => line.status !== "unchanged"
                ).length === 0 ? (
                  <p className="text-sm text-muted-foreground">
                    No line-level changes in this category.
                  </p>
                ) : null}
              </div>
            )}
          </div>
        ) : diffQuery.isLoading ? (
          <p className="text-sm text-muted-foreground">Loading diff…</p>
        ) : diffQuery.isError ? (
          <p className="text-sm text-danger">Could not load version diff.</p>
        ) : (
          <div className="flex flex-col gap-4 text-left">
            {summary ? (
              <div className="rounded-lg border border-border bg-muted/50 p-4 text-sm text-foreground/80">
                <p>
                  Grand total: {money(summary.before_grand_total)} →{" "}
                  {money(summary.after_grand_total)}
                </p>
                <p className="mt-1 text-muted-foreground">
                  {summary.added_count} added · {summary.removed_count} removed ·{" "}
                  {summary.changed_count} changed
                </p>
              </div>
            ) : null}
            <div className="flex flex-col gap-2">
              {categories
                .filter((row) => row.status !== "unchanged")
                .map((row) => (
                  <button
                    key={row.category_name}
                    type="button"
                    className="flex w-full items-center justify-between rounded border border-border px-3 py-2 text-left text-sm transition-colors hover:bg-muted/50"
                    onClick={() => setSelectedCategory(row.category_name)}
                  >
                    <span>{row.category_name}</span>
                    <span className="text-muted-foreground">
                      {money(row.before_total)} → {money(row.after_total)}
                    </span>
                  </button>
                ))}
            </div>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};
